import React from 'react'
import { Container, Box, Title, Text, Stack, Anchor, Group, Divider } from '@mantine/core'

export default function PrivacyPolicyPage() {
  const noteIcon = `${process.env.PUBLIC_URL}/tunistry-note-icon-white.svg`
  const EFFECTIVE_DATE = 'April 14, 2026'

  const Section = ({ title, children }) => (
    <Stack gap="sm" mb="xl">
      <Title order={3} style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: 4 }}>{title}</Title>
      {children}
    </Stack>
  )

  const P = ({ children }) => (
    <Text size="sm" style={{ lineHeight: 1.75 }}>{children}</Text>
  )

  const UL = ({ items }) => (
    <Box component="ul" style={{ paddingLeft: 24, margin: '8px 0' }}>
      {items.map((item, i) => (
        <Box component="li" key={i} style={{ marginBottom: 6 }}>
          <Text size="sm" style={{ lineHeight: 1.7 }}>{item}</Text>
        </Box>
      ))}
    </Box>
  )

  return (
    <Box>
      {/* Header */}
      <Box component="header" style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: 'rgba(0,0,0,0.4)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(255,255,255,0.08)'
      }}>
        <Container size="lg" py="sm">
          <Group justify="space-between" align="center">
            <Group align="center" gap="sm">
              <img src={noteIcon} alt="Tunistry note icon" width={28} height={28} style={{ display: 'block' }} />
              <Title order={2} style={{
                margin: 0, fontSize: 28, fontWeight: 700,
                fontFamily: 'Cairo, Arial, sans-serif', letterSpacing: 0.2,
              }}>
                Tunistry
              </Title>
            </Group>
          </Group>
        </Container>
      </Box>

      {/* Content */}
      <Container size="md" py={56} style={{ textAlign: 'left' }}>
        <Stack gap="xs" mb="xl">
          <Title order={1} style={{ fontSize: '2rem' }}>Privacy Policy</Title>
          <Text size="sm" c="dimmed">Effective Date: {EFFECTIVE_DATE}</Text>
        </Stack>

        <P>
          Tunistry ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy
          explains how we collect, use, disclose, and safeguard your information when you use the Tunistry
          platform, including our website, mobile applications, and any related services (collectively, the
          "Service"). Please read this policy carefully. By using the Service, you agree to the practices
          described herein.
        </P>

        <Divider my="xl" />

        <Section title="1. Information We Collect">
          <P><strong>Information you provide directly:</strong></P>
          <UL items={[
            'Account registration information, such as your name, email address, and password.',
            'Profile information, including any optional details you choose to provide (e.g., profile picture, username).',
            'Payment information, which is processed by our third-party payment processor. We do not store full payment card details.',
            'Communications you send us, including support requests and feedback.',
            'Content you create within the Service, including chat messages, room preferences, tags, reactions, and listening history.',
          ]} />
          <P><strong>Information collected automatically:</strong></P>
          <UL items={[
            'Device and browser information, including IP address, browser type, operating system, and device identifiers.',
            'Usage data, such as pages visited, features used, tracks played, and interactions with AI DJ features.',
            'Session data, including timestamps, referral URLs, and navigation patterns.',
            'Cookies and similar tracking technologies (see Section 6 below).',
          ]} />
          <P><strong>Information from third parties:</strong></P>
          <UL items={[
            'If you sign in using a third-party service (e.g., Apple, Google), we receive basic profile information permitted by your settings on that service.',
            'Analytics providers may supply aggregated, de-identified data about how users interact with the Service.',
          ]} />
        </Section>

        <Section title="2. How We Use Your Information">
          <P>We use the information we collect to:</P>
          <UL items={[
            'Provide, operate, and improve the Service.',
            'Personalize your experience, including training AI DJ models on your listening preferences, reactions, tags, and chat history.',
            'Process transactions and manage your account.',
            'Send administrative communications, such as account confirmations, security alerts, and policy updates.',
            'Send promotional communications, where permitted by law and in accordance with your preferences.',
            'Monitor and analyze usage trends to improve functionality and user experience.',
            'Detect and prevent fraud, abuse, and violations of our Terms of Service.',
            'Comply with legal obligations.',
          ]} />
        </Section>

        <Section title="3. How We Share Your Information">
          <P>We do not sell your personal information. We may share your information in the following circumstances:</P>
          <UL items={[
            'Service providers: We share information with vendors and contractors who perform services on our behalf, such as hosting, payment processing, analytics, and customer support. These parties are contractually obligated to protect your data.',
            'Other users: Content you post within a shared room (e.g., chat messages, reactions) is visible to other members of that room.',
            'Business transfers: If Tunistry is acquired, merged, or sells substantially all of its assets, your information may be transferred as part of that transaction.',
            'Legal compliance: We may disclose your information if required by law, subpoena, court order, or to protect the rights, property, or safety of Tunistry, our users, or the public.',
            'With your consent: We may share your information for any other purpose with your explicit consent.',
          ]} />
        </Section>

        <Section title="4. Data Retention">
          <P>
            We retain your personal information for as long as your account is active or as needed to provide the
            Service. We also retain data as necessary to comply with legal obligations, resolve disputes, and
            enforce our agreements. When you delete your account, we will delete or anonymize your personal data
            within 90 days, except where retention is required by law.
          </P>
        </Section>

        <Section title="5. Your Privacy Rights">
          <P>
            Depending on where you reside, you may have the following rights regarding your personal information:
          </P>
          <UL items={[
            'Access: Request a copy of the personal data we hold about you.',
            'Correction: Request correction of inaccurate or incomplete data.',
            'Deletion: Request deletion of your personal data, subject to certain exceptions.',
            'Portability: Receive your data in a structured, machine-readable format.',
            'Opt-out of marketing: Unsubscribe from promotional emails at any time via the link in the email or by contacting us.',
            'Do Not Track: We honor browser Do Not Track signals for analytics tracking.',
          ]} />
          <P>
            California residents may have additional rights under the California Consumer Privacy Act (CCPA).
            EU/EEA/UK residents may have additional rights under the General Data Protection Regulation (GDPR)
            or UK GDPR. To exercise any of these rights, contact us at privacy@tunistry.com.
          </P>
        </Section>

        <Section title="6. Cookies and Tracking Technologies">
          <P>
            We use cookies and similar technologies (such as local storage and session tokens) to operate the
            Service, remember your preferences, maintain authentication sessions, and understand how users
            interact with the platform. These are categorized as:
          </P>
          <UL items={[
            'Strictly necessary cookies: Required for the Service to function. You cannot opt out of these.',
            'Analytics cookies: Help us understand usage patterns. You may opt out by adjusting your browser settings or using our cookie preferences (where available).',
            'Preference cookies: Remember your settings and choices.',
          ]} />
          <P>
            Most browsers allow you to control cookies through their settings. Disabling certain cookies
            may affect the functionality of the Service.
          </P>
        </Section>

        <Section title="7. Data Security">
          <P>
            We implement industry-standard security measures including encryption in transit (TLS), hashed
            password storage, and access controls to protect your personal information. However, no method of
            transmission or storage is 100% secure, and we cannot guarantee absolute security. You are
            responsible for keeping your account credentials confidential.
          </P>
        </Section>

        <Section title="8. Children's Privacy">
          <P>
            The Service is not directed to children under 13 years of age (or the applicable age of digital
            consent in your jurisdiction). We do not knowingly collect personal information from children. If
            you believe we have inadvertently collected such information, please contact us at
            privacy@tunistry.com and we will promptly delete it.
          </P>
        </Section>

        <Section title="9. International Data Transfers">
          <P>
            Tunistry is operated from the United States. If you access the Service from outside the United
            States, your information may be transferred to, stored, and processed in the United States or other
            countries where our service providers operate. By using the Service, you consent to this transfer.
            Where required, we rely on appropriate legal mechanisms (such as Standard Contractual Clauses) to
            facilitate international data transfers.
          </P>
        </Section>

        <Section title="10. Third-Party Links and Services">
          <P>
            The Service may contain links to third-party websites or services. This Privacy Policy does not
            apply to those third parties. We encourage you to review the privacy policies of any third-party
            services you access.
          </P>
        </Section>

        <Section title="11. YouTube API Services">
          <P>
            Tunistry uses <strong>YouTube API Services</strong> to provide certain music discovery and playback
            features within the Service. By using these features, your interactions with YouTube content are
            subject to Google's data practices. We encourage you to review the{' '}
            <Anchor href="http://www.google.com/policies/privacy" target="_blank" rel="noopener noreferrer">
              Google Privacy Policy
            </Anchor>{' '}
            to understand how Google collects and uses your information when you interact with YouTube content
            through the Service. Use of YouTube API Services through the Service is also subject to the{' '}
            <Anchor href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">
              YouTube Terms of Service
            </Anchor>.
          </P>
        </Section>

        <Section title="12. Changes to This Policy">
          <P>
            We may update this Privacy Policy from time to time. We will notify you of material changes by
            posting the updated policy on our website and, where required by law, by sending you an email
            notification. Your continued use of the Service after the effective date of the revised policy
            constitutes your acceptance of the changes.
          </P>
        </Section>

        <Section title="13. Contact Us">
          <P>
            If you have questions, concerns, or requests regarding this Privacy Policy or our data practices,
            please contact us at:
          </P>
          <P>
            <strong>Tunistry</strong><br />
            Email: <Anchor href="mailto:privacy@tunistry.com">privacy@tunistry.com</Anchor>
          </P>
        </Section>

        <Divider my="xl" />

        <Group gap="lg">
          <Anchor size="sm" href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Anchor>
        </Group>
      </Container>

      {/* Footer */}
      <Box style={{ borderTop: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.2)' }}>
        <Container size="lg" py="lg">
          <Group justify="center" gap="xl">
            <Text size="xs" c="dimmed">© {new Date().getFullYear()} Tunistry. All rights reserved.</Text>
            <Anchor size="xs" c="dimmed" href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Anchor>
            <Anchor size="xs" c="dimmed" href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Anchor>
          </Group>
        </Container>
      </Box>
    </Box>
  )
}
